"use client";

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type MouseEvent,
} from "react";
import type { Route } from "next";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";

import PostGrid, {
  type PostContextLabel,
  type PostNode,
} from "@/app/components/PostGrid";
import h2NotesCopy from "@/messages/mandarin-h2-notes-controls.json";
import rolloutCopy from "@/messages/mandarin-rollout.json";
import { parsePublicPathname } from "@/lib/i18n/paths";
import { event } from "@/lib/gtag";
import { postUrl } from "@/lib/post-url";
import {
  buildGroupedProgrammePosts,
  buildNotesResourcePostGroups,
  buildProgrammeCategories,
  deriveProgrammeLevelFacet,
  filterProgrammePosts,
  getCategoryDisplayLabel,
  getGroupHeaderTheme,
  getProgrammeTopicNumberLabel,
  isSyllabusSectionCategory,
  resolveInitialProgrammeCategory,
  resolveInitialProgrammeLevel,
  resolveProgrammeCategoryFromParam,
  resolveProgrammeLevelFromParam,
  shouldRenderGroupedProgrammePosts,
} from "@/lib/blog/programme-posts-state";
import type { ProgrammePostGroup } from "./programme-post-groups";
import type { H2StudyRoute } from "@/lib/study/h2-study-types";

const NOTES_HUB_H2_CLASS =
  "text-balance text-[2rem] font-bold leading-tight text-slate-950 sm:text-[2.5rem] lg:text-5xl";
// Topic notes show six cards at a time. Every page stays in the server HTML
// (later pages carry the `hidden` attribute) so crawlers see a plain link to
// each note; the Previous and Next buttons only switch which page is visible.
const NOTES_TOPIC_PAGE_SIZE = 6;
const PAGINATION_LINK_CLASS =
  "inline-flex items-center gap-1.5 text-slate-700 no-underline transition hover:text-slate-950";
const PAGINATION_DISABLED_CLASS =
  "inline-flex cursor-not-allowed items-center gap-1.5 text-slate-300";

interface ProgrammePostsProps {
  posts: Array<PostNode & { subject?: string | null }>;
  postGroups?: ReadonlyArray<ProgrammePostGroup>;
  pinnedPosts?: ReadonlyArray<{ href: string; label: string }>;
  basePath: string;
  initialCategory?: string | null;
  initialLevel?: "upper-sec" | "h2" | null;
  heading?: string | null;
  headingLevel?: "h2" | "h3";
  description?: string;
  /**
   * Page size for large hub libraries. Pages are real `?page=N` URLs that the
   * hub route server-renders (pass `initialPage`); the Previous and Next links
   * switch pages in place once JavaScript has loaded.
   */
  pageSize?: number;
  initialPage?: number;
  sectionClassName?: string;
  headingClassName?: string;
  showCategories?: boolean; // whether to show category pills + filter
  showLevelPill?: boolean; // whether to show level badge on cards
  showLevelFacet?: boolean; // whether to show level filter controls
  groupNotesResources?: boolean; // split notes hubs into topic notes and study guides
  topicEyebrowMode?: "fallback" | "explicit";
  syllabusToc?: ReadonlyArray<{
    title: string;
    href: string;
    children?: ReadonlyArray<{ title: string; href: string }>;
  }>;
  tuitionPracticalImageSrc?: string;
  editorialFolio?: boolean; // use the quiet text-led directory treatment on commercial hubs
  h2StudyRoutes?: ReadonlyArray<H2StudyRoute>;
}

type ProgrammePost = ProgrammePostsProps["posts"][number];

function programmePostUrl(post: ProgrammePost) {
  return postUrl(post as Parameters<typeof postUrl>[0]);
}

function isOverviewTopicPost(post: ProgrammePost) {
  return [post._sys.filename, post.title ?? ""].some((source) =>
    /\boverview\b/i.test(source)
  );
}

function buildTopicPostEyebrows(
  posts: ProgrammePost[],
  mode: "fallback" | "explicit",
  syllabusToc?: ReadonlyArray<{
    title: string;
    href: string;
    children?: ReadonlyArray<{ title: string; href: string }>;
  }>
) {
  const syllabusEntries = (syllabusToc ?? []).flatMap((entry) => [
    entry,
    ...(entry.children ?? []),
  ]);
  const syllabusContextByFilename = new Map(
    syllabusEntries.flatMap((entry) => {
      const filename = entry.href.split("/").filter(Boolean).at(-1);
      if (!filename) return [];

      const numbered = entry.title.match(/^(\d+(?:\.\d+)?)\)\s*(.+)$/);
      if (numbered) {
        const identifier = numbered[1];
        return [
          [
            filename,
            {
              label: identifier.includes(".")
                ? identifier
                : `Topic ${identifier}`,
              detail: numbered[2],
            },
          ] as const,
        ];
      }

      const biology = entry.title.match(
        /^((?:Core\s+\d+)|(?:Extension\s+[A-Z]))\)\s*(.+)$/i
      );
      if (biology) {
        return [[filename, { label: biology[1], detail: biology[2] }] as const];
      }

      return [];
    })
  );

  return new Map(
    posts.flatMap((post, index) => {
      const syllabusContext = syllabusContextByFilename.get(post._sys.filename);
      if (syllabusContext) {
        return [[post._sys.filename, syllabusContext] as const];
      }

      const explicitTopicNumber = getProgrammeTopicNumberLabel(post);
      if (!explicitTopicNumber && isOverviewTopicPost(post)) return [];

      const label = explicitTopicNumber
        ? `Topic ${explicitTopicNumber}`
        : mode === "explicit"
        ? "Supplement"
        : `Topic ${index + 1}`;
      return [
        [post._sys.filename, { label } satisfies PostContextLabel] as const,
      ];
    })
  );
}

export default function ProgrammePosts({
  posts,
  postGroups,
  pinnedPosts,
  basePath,
  initialCategory,
  initialLevel,
  heading: suppliedHeading,
  headingLevel = "h2",
  description,
  pageSize,
  initialPage = 1,
  sectionClassName = "mb-16 sm:mb-20",
  headingClassName,
  showCategories = true,
  showLevelPill = false,
  showLevelFacet = true,
  groupNotesResources = false,
  topicEyebrowMode = "fallback",
  syllabusToc,
  editorialFolio = false,
  tuitionPracticalImageSrc,
  h2StudyRoutes = [],
}: ProgrammePostsProps) {
  const locale = parsePublicPathname(basePath).locale;
  const isMandarin = locale === "zh-Hans";
  const copy = rolloutCopy.programmeDiscovery;
  const heading =
    suppliedHeading === undefined
      ? isMandarin
        ? copy.heading
        : "Explore This Hub"
      : suppliedHeading;
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const categories = useMemo(() => {
    return buildProgrammeCategories({
      posts,
      showCategories,
    });
  }, [posts, showCategories]);
  const h2StudyRoutesByFilename = useMemo(
    () =>
      new Map(
        h2StudyRoutes.map((route) => [route.noteHref.split("/").at(-1)!, route])
      ),
    [h2StudyRoutes]
  );
  const usesH2TopicResourceFilters = h2StudyRoutes.length > 0;

  const [activeCategory, setActiveCategory] = useState<string | undefined>(() =>
    resolveInitialProgrammeCategory({
      initialCategory,
      categories,
      showCategories,
    })
  );

  const { hasUpperSec, hasH2, hasLevelFacet } = useMemo(
    () =>
      deriveProgrammeLevelFacet({
        posts,
        showLevelFacet,
      }),
    [posts, showLevelFacet]
  );

  const [activeLevel, setActiveLevel] = useState<
    "upper-sec" | "h2" | undefined
  >(() =>
    resolveInitialProgrammeLevel({
      initialLevel,
      hasLevelFacet,
    })
  );
  const [topicNotesPage, setTopicNotesPage] = useState(1);
  const [postGridPage, setPostGridPage] = useState(() =>
    Math.max(1, Math.floor(initialPage))
  );

  useEffect(() => {
    setActiveCategory(
      resolveProgrammeCategoryFromParam({
        categoryParam: searchParams.get("category"),
        categories,
        showCategories,
      })
    );
  }, [searchParams, categories, showCategories]);

  useEffect(() => {
    setActiveLevel(
      resolveProgrammeLevelFromParam({
        levelParam: searchParams.get("level"),
        hasLevelFacet,
      })
    );
  }, [searchParams, hasLevelFacet]);

  const filteredPosts = useMemo(() => {
    return filterProgrammePosts({
      posts,
      activeCategory,
      activeLevel,
      showCategories,
    });
  }, [posts, activeLevel, activeCategory, showCategories]);

  const pinnedPostGroups = useMemo(() => {
    if (!pinnedPosts?.length || activeCategory || activeLevel) {
      return { pinned: [], remaining: filteredPosts };
    }

    const pinnedHrefSet = new Set(pinnedPosts.map((post) => post.href));
    const pinnedByHref = new Map(
      filteredPosts.map((post) => [programmePostUrl(post), post] as const)
    );
    const pinned = pinnedPosts
      .map((post) => pinnedByHref.get(post.href))
      .filter((post): post is (typeof filteredPosts)[number] => Boolean(post));

    if (pinned.length === 0) {
      return { pinned: [], remaining: filteredPosts };
    }

    return {
      pinned,
      remaining: filteredPosts.filter(
        (post) => !pinnedHrefSet.has(programmePostUrl(post))
      ),
    };
  }, [activeCategory, activeLevel, filteredPosts, pinnedPosts]);

  const groupedPosts = useMemo(() => {
    const groups = buildGroupedProgrammePosts({ posts, postGroups });
    return isMandarin
      ? groups?.map((group) =>
          group.subject === "__other__"
            ? {
                ...group,
                title: "更多资源",
                description: "尚未归入特定学习方向的其他文章。",
              }
            : group
        ) ?? null
      : groups;
  }, [posts, postGroups, isMandarin]);

  const shouldRenderGrouped = shouldRenderGroupedProgrammePosts({
    groupedPosts,
    activeCategory,
    activeLevel,
  });
  const notesResourceGroups = useMemo(() => {
    if (!groupNotesResources || shouldRenderGrouped) return [];
    return buildNotesResourcePostGroups({ posts: filteredPosts });
  }, [filteredPosts, groupNotesResources, shouldRenderGrouped]);

  // Reset to the first page only when a filter actually changes, so a
  // server-rendered `?page=N` view is not reset on hydration.
  const filterKey = `${activeCategory ?? ""}|${activeLevel ?? ""}`;
  const previousFilterKey = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKey.current === filterKey) return;
    previousFilterKey.current = filterKey;
    setTopicNotesPage(1);
    setPostGridPage(1);
  }, [filterKey]);

  const postGridPageCount = pageSize
    ? Math.max(1, Math.ceil(filteredPosts.length / pageSize))
    : 1;
  const currentPostGridPage = Math.min(postGridPage, postGridPageCount);
  const visiblePostGridPosts = pageSize
    ? filteredPosts.slice(
        (currentPostGridPage - 1) * pageSize,
        currentPostGridPage * pageSize
      )
    : filteredPosts;

  useEffect(() => {
    setPostGridPage((currentPage) => Math.min(currentPage, postGridPageCount));
  }, [postGridPageCount]);

  useEffect(() => {
    const topicNotesGroup = notesResourceGroups.find(
      (group) => group.id === "topic-notes"
    );
    const pageCount = topicNotesGroup
      ? Math.ceil(topicNotesGroup.posts.length / NOTES_TOPIC_PAGE_SIZE)
      : 1;

    setTopicNotesPage((currentPage) => Math.min(currentPage, pageCount || 1));
  }, [notesResourceGroups]);

  const replaceFilterUrl = (key: "category" | "level", value?: string) => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    // A filter change returns the library to its first page.
    url.searchParams.delete("page");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  };

  const postGridPageHref = (page: number) => {
    const query: Record<string, string> = {};
    if (activeLevel) query.level = activeLevel;
    if (activeCategory) query.category = activeCategory;
    if (page > 1) query.page = String(page);
    return Object.keys(query).length > 0
      ? { pathname: basePath, query }
      : { pathname: basePath };
  };

  const handlePostGridPage = (
    clickEvent: MouseEvent<HTMLAnchorElement>,
    page: number
  ) => {
    // Plain links for crawlers and no-JS visitors; switch in place otherwise.
    if (
      clickEvent.metaKey ||
      clickEvent.ctrlKey ||
      clickEvent.shiftKey ||
      clickEvent.altKey ||
      clickEvent.button !== 0
    ) {
      return;
    }
    clickEvent.preventDefault();
    setPostGridPage(page);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (page > 1) {
      url.searchParams.set("page", String(page));
    } else {
      url.searchParams.delete("page");
    }
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  };

  const handleSelect = (slug?: string) => {
    event("tool_filter_change", {
      tool: "programme_posts",
      hub: basePath,
      filter: "category",
      value: slug ?? "all",
    });
    startTransition(() => {
      setActiveCategory(slug);
      replaceFilterUrl("category", slug);
    });
  };

  const handleSelectLevel = (level?: "upper-sec" | "h2") => {
    event("tool_filter_change", {
      tool: "programme_posts",
      hub: basePath,
      filter: "level",
      value: level ?? "all",
    });
    startTransition(() => {
      setActiveLevel(level);
      replaceFilterUrl("level", level);
    });
  };

  const categoryFilterClassName = usesH2TopicResourceFilters
    ? "not-prose mb-8 flex flex-wrap gap-2.5"
    : groupNotesResources
    ? "not-prose mb-12 grid grid-cols-2 gap-x-8 gap-y-3 text-base font-semibold sm:grid-cols-3 lg:grid-cols-4"
    : editorialFolio
    ? "not-prose mb-12 flex flex-wrap items-center gap-x-7 gap-y-3 text-base"
    : "not-prose mb-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm";
  const filterLinkBaseClassName = usesH2TopicResourceFilters
    ? "inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2.5 text-base font-semibold leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    : groupNotesResources || editorialFolio
    ? "leading-snug text-slate-700 transition hover:text-slate-950"
    : "transition hover:text-slate-900";
  const inactiveFilterClassName = usesH2TopicResourceFilters
    ? "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900"
    : groupNotesResources || editorialFolio
    ? "text-slate-700"
    : "text-slate-500";

  const categoryEmoji = (slug: string, label: string) => {
    const value = `${slug} ${label}`.toLowerCase();
    if (value.includes("exam")) return "📝";
    if (value.includes("strategy") || value.includes("study")) return "🎯";
    if (value.includes("mechanic") || value.includes("force")) return "⚙️";
    if (value.includes("electric") || value.includes("magnet")) return "⚡";
    if (value.includes("thermal") || value.includes("temperature")) return "🌡️";
    if (value.includes("wave") || value.includes("oscillation")) return "〰️";
    if (value.includes("modern") || value.includes("quantum")) return "⚛️";
    if (value.includes("calculus")) return "∫";
    if (value.includes("probability") || value.includes("statistic"))
      return "🎲";
    if (value.includes("vector")) return "↗️";
    if (value.includes("complex")) return "𝑖";
    if (value.includes("sequence") || value.includes("series")) return "🔢";
    if (value.includes("function") || value.includes("graph")) return "📈";
    if (value.includes("organic")) return "🧬";
    if (value.includes("inorganic")) return "🧪";
    if (value.includes("physical")) return "⚗️";
    if (value.includes("cell") || value.includes("biomolecule")) return "🔬";
    if (value.includes("genetic")) return "🧬";
    if (value.includes("evolution")) return "🌿";
    if (value.includes("energy") || value.includes("equilibrium")) return "⚖️";
    if (value.includes("extension")) return "🚀";
    if (value.includes("core")) return "📚";
    return "📖";
  };

  return (
    <section
      id="related-posts"
      className={sectionClassName}
      aria-label={
        heading === null
          ? isMandarin
            ? copy.supporting
            : "Supporting study guides"
          : undefined
      }
    >
      {heading === null ? null : headingLevel === "h3" ? (
        <h3
          className={
            headingClassName ??
            "mb-6 text-2xl font-semibold text-slate-900 sm:text-3xl"
          }
        >
          {heading}
        </h3>
      ) : (
        <h2
          className={
            headingClassName ??
            "mb-8 text-3xl font-bold text-slate-900 sm:text-4xl"
          }
        >
          {heading}
        </h2>
      )}
      {description && (
        <p className="mb-8 text-base text-slate-600">{description}</p>
      )}

      {hasLevelFacet && (
        <div className="not-prose mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <Link
            href={{ pathname: basePath }}
            rel="nofollow"
            prefetch={false}
            scroll={false}
            onClick={(e) => {
              e.preventDefault();
              handleSelectLevel(undefined);
            }}
            className={clsx(
              "transition hover:text-slate-900",
              !activeLevel
                ? "font-semibold text-slate-900 underline underline-offset-4 decoration-2"
                : "text-slate-500"
            )}
            aria-current={!activeLevel ? "page" : undefined}
          >
            {isMandarin ? copy.allLevels : "All levels"}
          </Link>
          {hasUpperSec && (
            <Link
              href={{ pathname: basePath, query: { level: "upper-sec" } }}
              rel="nofollow"
              prefetch={false}
              scroll={false}
              onClick={(e) => {
                e.preventDefault();
                handleSelectLevel("upper-sec");
              }}
              className={clsx(
                "transition hover:text-slate-900",
                activeLevel === "upper-sec"
                  ? "font-semibold text-slate-900 underline underline-offset-4 decoration-2"
                  : "text-slate-500"
              )}
              aria-current={activeLevel === "upper-sec" ? "page" : undefined}
            >
              {isMandarin ? copy.upperSec : "Upper Sec"}
            </Link>
          )}
          {hasH2 && (
            <Link
              href={{ pathname: basePath, query: { level: "h2" } }}
              rel="nofollow"
              prefetch={false}
              scroll={false}
              onClick={(e) => {
                e.preventDefault();
                handleSelectLevel("h2");
              }}
              className={clsx(
                "transition hover:text-slate-900",
                activeLevel === "h2"
                  ? "font-semibold text-slate-900 underline underline-offset-4 decoration-2"
                  : "text-slate-500"
              )}
              aria-current={activeLevel === "h2" ? "page" : undefined}
            >
              H2
            </Link>
          )}
        </div>
      )}

      {showCategories && categories.length > 0 && (
        <div className={categoryFilterClassName}>
          <Link
            href={{ pathname: basePath }}
            rel="nofollow"
            prefetch={false}
            scroll={false}
            onClick={(event) => {
              event.preventDefault();
              handleSelect(undefined);
            }}
            className={clsx(
              filterLinkBaseClassName,
              !activeCategory
                ? usesH2TopicResourceFilters
                  ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                  : "font-semibold text-slate-900 underline underline-offset-4 decoration-2"
                : inactiveFilterClassName
            )}
            aria-current={!activeCategory ? "page" : undefined}
          >
            {usesH2TopicResourceFilters ? (
              <span aria-hidden="true">✦</span>
            ) : null}
            {isMandarin ? copy.all : "All"}
          </Link>
          {categories.map((category, i) => {
            const isActive = category.slug === activeCategory;
            const prev = categories[i - 1];
            // Insert a subtle divider between the last syllabus-section
            // pill and the first content-type pill so the two groups
            // read as distinct.
            const needsDivider =
              prev &&
              isSyllabusSectionCategory(prev.label) &&
              !isSyllabusSectionCategory(category.label);
            return (
              <Fragment key={category.slug}>
                {needsDivider && (
                  <span
                    aria-hidden
                    className={clsx(
                      "select-none text-slate-300",
                      groupNotesResources && "hidden"
                    )}
                  >
                    |
                  </span>
                )}
                <Link
                  href={{
                    pathname: basePath,
                    query: { category: category.slug },
                  }}
                  rel="nofollow"
                  prefetch={false}
                  scroll={false}
                  onClick={(event) => {
                    event.preventDefault();
                    handleSelect(category.slug);
                  }}
                  className={clsx(
                    filterLinkBaseClassName,
                    isActive
                      ? usesH2TopicResourceFilters
                        ? "border-blue-700 bg-blue-700 text-white shadow-sm"
                        : "font-semibold text-slate-900 underline underline-offset-4 decoration-2"
                      : inactiveFilterClassName
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  {usesH2TopicResourceFilters ? (
                    <span aria-hidden="true">
                      {categoryEmoji(category.slug, category.label)}
                    </span>
                  ) : null}
                  {isMandarin
                    ? copy.categories[
                        category.label as keyof typeof copy.categories
                      ] ??
                      `${getCategoryDisplayLabel(category.label)}（英文分类）`
                    : getCategoryDisplayLabel(category.label)}
                </Link>
              </Fragment>
            );
          })}
        </div>
      )}

      {shouldRenderGrouped ? (
        <div className="space-y-12">
          {groupedPosts!.map((group) => {
            const theme = getGroupHeaderTheme(group.subject);
            return (
              <section key={group.subject} className="space-y-4">
                <div className="not-prose">
                  {group.href ? (
                    <Link<string>
                      href={group.href as Route<string>}
                      prefetch={false}
                      className={clsx(
                        editorialFolio
                          ? "group block no-underline transition hover:text-blue-800 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
                          : "group block rounded-xl border px-5 py-4 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-2",
                        editorialFolio ? undefined : theme.container
                      )}
                    >
                      <h3
                        className={clsx(
                          editorialFolio
                            ? "m-0 text-2xl font-semibold tracking-[-0.02em] text-slate-950 sm:text-3xl"
                            : "m-0 text-lg font-semibold group-hover:underline",
                          editorialFolio ? undefined : theme.title
                        )}
                      >
                        {group.title}
                      </h3>
                    </Link>
                  ) : (
                    <div
                      className={clsx(
                        editorialFolio
                          ? ""
                          : "rounded-xl border px-5 py-4 text-center shadow-sm",
                        editorialFolio ? undefined : theme.container
                      )}
                    >
                      <h3
                        className={clsx(
                          editorialFolio
                            ? "m-0 text-2xl font-semibold tracking-[-0.02em] text-slate-950 sm:text-3xl"
                            : "m-0 text-lg font-semibold",
                          editorialFolio ? undefined : theme.title
                        )}
                      >
                        {group.title}
                      </h3>
                    </div>
                  )}
                </div>
                {group.description ? (
                  <p
                    className={clsx(
                      "not-prose m-0 text-base text-slate-700",
                      editorialFolio
                        ? "max-w-3xl text-lg leading-8"
                        : "text-center"
                    )}
                  >
                    {group.description}
                  </p>
                ) : null}
                <PostGrid
                  locale={locale}
                  posts={group.posts}
                  showLevelPill={showLevelPill}
                  hideCategoryPill={!showCategories || editorialFolio}
                  editorial={groupNotesResources}
                  editorialFolio={editorialFolio}
                  tuitionPracticalImageSrc={tuitionPracticalImageSrc}
                />
              </section>
            );
          })}
        </div>
      ) : notesResourceGroups.length > 0 ? (
        <div className="space-y-12">
          {notesResourceGroups.map((group) => {
            const isTopicNotesGroup = group.id === "topic-notes";
            const pageCount = isTopicNotesGroup
              ? Math.ceil(group.posts.length / NOTES_TOPIC_PAGE_SIZE)
              : 1;
            const currentPage = Math.min(topicNotesPage, pageCount || 1);
            const postPages =
              isTopicNotesGroup && pageCount > 1
                ? Array.from({ length: pageCount }, (_, pageIndex) =>
                    group.posts.slice(
                      pageIndex * NOTES_TOPIC_PAGE_SIZE,
                      (pageIndex + 1) * NOTES_TOPIC_PAGE_SIZE
                    )
                  )
                : [group.posts];
            const postEyebrows = isTopicNotesGroup
              ? buildTopicPostEyebrows(
                  group.posts,
                  topicEyebrowMode,
                  syllabusToc
                )
              : undefined;

            return (
              <section id={group.id} key={group.id} className="space-y-4">
                <div className="not-prose max-w-3xl">
                  <h2
                    className={clsx(
                      "m-0",
                      isTopicNotesGroup && h2StudyRoutes.length > 0
                        ? "sr-only"
                        : NOTES_HUB_H2_CLASS
                    )}
                  >
                    {isMandarin
                      ? isTopicNotesGroup
                        ? h2NotesCopy.topicNotes
                        : h2NotesCopy.studyGuides
                      : group.title}
                  </h2>
                </div>
                {postPages.map((pagePosts, pageIndex) => {
                  const grid = (
                    <PostGrid
                      locale={locale}
                      posts={pagePosts}
                      showLevelPill={showLevelPill}
                      hideCategoryPill={isTopicNotesGroup || !showCategories}
                      editorial
                      postEyebrows={postEyebrows}
                      h2StudyRoutesByFilename={
                        isTopicNotesGroup ? h2StudyRoutesByFilename : undefined
                      }
                      prioritizeFirstImage={pageIndex === 0}
                    />
                  );
                  return postPages.length > 1 ? (
                    <div
                      key={pageIndex}
                      data-topic-notes-page={pageIndex + 1}
                      hidden={pageIndex + 1 !== currentPage}
                    >
                      {grid}
                    </div>
                  ) : (
                    <Fragment key={pageIndex}>{grid}</Fragment>
                  );
                })}
                {isTopicNotesGroup && pageCount > 1 ? (
                  <nav
                    aria-label={
                      isMandarin
                        ? copy.topicPagination
                        : "Topic notes pagination"
                    }
                    className="not-prose flex items-center justify-between gap-4 pt-2 text-base font-semibold text-slate-900"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-slate-700 transition hover:text-slate-950 disabled:cursor-not-allowed disabled:text-slate-300"
                      onClick={() =>
                        setTopicNotesPage((page) => Math.max(1, page - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      {isMandarin ? copy.previous : "Previous"}
                    </button>
                    <span className="text-sm font-semibold text-slate-600">
                      {isMandarin
                        ? `第 ${currentPage} 页，共 ${pageCount} 页`
                        : `Page ${currentPage} of ${pageCount}`}
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-slate-700 transition hover:text-slate-950 disabled:cursor-not-allowed disabled:text-slate-300"
                      onClick={() =>
                        setTopicNotesPage((page) =>
                          Math.min(pageCount, page + 1)
                        )
                      }
                      disabled={currentPage === pageCount}
                    >
                      {isMandarin ? copy.next : "Next"}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </nav>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : filteredPosts.length > 0 ? (
        pinnedPostGroups.pinned.length > 0 ? (
          <div className="space-y-12">
            <section className="space-y-4">
              <div className="not-prose max-w-3xl">
                <h3 className="m-0 text-lg font-semibold text-slate-900 sm:text-xl">
                  {isMandarin ? copy.recommended : "Recommended first"}
                </h3>
              </div>
              <PostGrid
                locale={locale}
                posts={pinnedPostGroups.pinned}
                showLevelPill={showLevelPill}
                hideCategoryPill={!showCategories || editorialFolio}
                editorial={groupNotesResources}
                editorialFolio={editorialFolio}
                tuitionPracticalImageSrc={tuitionPracticalImageSrc}
              />
            </section>
            {pinnedPostGroups.remaining.length > 0 ? (
              <section className="space-y-4">
                <div className="not-prose max-w-3xl">
                  <h3 className="m-0 text-lg font-semibold text-slate-900 sm:text-xl">
                    {isMandarin ? copy.allPractical : "All practical guides"}
                  </h3>
                </div>
                <PostGrid
                  locale={locale}
                  posts={pinnedPostGroups.remaining}
                  showLevelPill={showLevelPill}
                  hideCategoryPill={!showCategories || editorialFolio}
                  editorial={groupNotesResources}
                  editorialFolio={editorialFolio}
                  tuitionPracticalImageSrc={tuitionPracticalImageSrc}
                />
              </section>
            ) : null}
          </div>
        ) : (
          <>
            <PostGrid
              locale={locale}
              posts={visiblePostGridPosts}
              showLevelPill={showLevelPill}
              hideCategoryPill={!showCategories || editorialFolio}
              editorial={groupNotesResources}
              editorialFolio={editorialFolio}
              tuitionPracticalImageSrc={tuitionPracticalImageSrc}
            />
            {postGridPageCount > 1 ? (
              <nav
                aria-label={
                  isMandarin ? copy.pagination : "Hub library pagination"
                }
                className="not-prose mt-10 flex items-center justify-between gap-4 border-y border-slate-300 py-5 text-base font-semibold text-slate-900"
              >
                {currentPostGridPage > 1 ? (
                  <Link
                    href={postGridPageHref(currentPostGridPage - 1)}
                    rel="prev"
                    prefetch={false}
                    scroll={false}
                    onClick={(clickEvent) =>
                      handlePostGridPage(clickEvent, currentPostGridPage - 1)
                    }
                    className={PAGINATION_LINK_CLASS}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    {isMandarin ? copy.previous : "Previous"}
                  </Link>
                ) : (
                  <span
                    aria-disabled="true"
                    className={PAGINATION_DISABLED_CLASS}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    {isMandarin ? copy.previous : "Previous"}
                  </span>
                )}
                <span className="text-sm font-semibold text-slate-600">
                  {isMandarin
                    ? `第 ${currentPostGridPage} 页，共 ${postGridPageCount} 页`
                    : `${currentPostGridPage} of ${postGridPageCount}`}
                </span>
                {currentPostGridPage < postGridPageCount ? (
                  <Link
                    href={postGridPageHref(currentPostGridPage + 1)}
                    rel="next"
                    prefetch={false}
                    scroll={false}
                    onClick={(clickEvent) =>
                      handlePostGridPage(clickEvent, currentPostGridPage + 1)
                    }
                    className={PAGINATION_LINK_CLASS}
                  >
                    {isMandarin ? copy.next : "Next"}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                ) : (
                  <span
                    aria-disabled="true"
                    className={PAGINATION_DISABLED_CLASS}
                  >
                    {isMandarin ? copy.next : "Next"}
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
              </nav>
            ) : null}
          </>
        )
      ) : (
        <p className="text-base text-gray-600">
          {isPending
            ? isMandarin
              ? copy.updating
              : "Updating posts…"
            : isMandarin
            ? copy.empty
            : "No posts in this category yet-check back soon."}
        </p>
      )}
    </section>
  );
}
